import moment from 'moment';
import React from 'react';
import Card from 'react-bootstrap/Card';
import { isAllLocations, getLocationName, getLocationBadgeColor } from '../../../util/locationUtils';
import { getTechnicianColor, getStageColor, getEventBands, buildBandGradient, getWashFromAccent } from '../../../util/colorCodingUtils';
import { getTaskTypeMeta } from '../../../constants/taskTypeMeta';
import { isMarineVehicle } from '../../../util/assetType';

// Constants
const DATE_FORMAT = "ddd Do MMM";

// FIXED: Robust string parsing function
const parseJobInfo = (workString) => {
    try {
        // More robust parsing with fallbacks
        const forIndex = workString.lastIndexOf(' for ');
        const onIndex = workString.indexOf(' on ');
        
        let customerName = 'Customer';
        let serviceName = workString;
        let vehicleInfo = 'Vehicle info not available';
        
        if (forIndex > -1) {
            customerName = workString.substring(forIndex + 5).trim();
            serviceName = workString.substring(0, forIndex).trim();
        }
        
        if (onIndex > -1 && onIndex < forIndex) {
            serviceName = workString.substring(0, onIndex).trim();
            const vehicleAndDealer = workString.substring(onIndex + 4, forIndex).trim();
            vehicleInfo = vehicleAndDealer;
        }
        
        return { customerName, serviceName, vehicleInfo };
    } catch (error) {
        console.error('Error parsing job info:', error);
        return {
            customerName: 'Customer',
            serviceName: workString,
            vehicleInfo: 'Vehicle info not available'
        };
    }
};

const CalendarCard = ({ date, workTable, onDisplayDetails, onTaskClick, isDesktop, isExpanded, onCardExpansion, locations, selectedLocationId, colorMode, stageColors, technicians, selectedTechnician }) => {
    const showLocationBadge = isAllLocations(selectedLocationId) && Array.isArray(locations) && locations.length > 1;
    // Filter jobs for this specific date
    const dayJobs = workTable.filter(job => job.startDateString === date);
const unsyncedCount = dayJobs.filter(job =>
    !job.isTask && !job.isInCalendar && moment(job.startDate).isSameOrAfter(moment().startOf('day'))
).length;
    const isToday = date === moment().format(DATE_FORMAT);

    const handleCardClick = () => {
        onCardExpansion(date);
    };

    const handleJobClick = (job) => {
        onDisplayDetails(job.requestId);
    };

    return (
        <Card className={`day-card ${isExpanded ? 'expanded' : ''} ${isToday ? 'today' : ''}`}>
            <Card.Header className="day-header" onClick={handleCardClick}>
                <div className="day-date">
                    <div className="day-title">
                        {isToday ? `Today - ${date}` : date}
                        {dayJobs.length > 0 && <span className="day-count"> ({dayJobs.length})</span>}
                    </div>
                    <div className="expand-icon"><i className={`fa-sharp fa-regular ${isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i></div>
                </div>
                {unsyncedCount > 0 && (
                    <div className="day-summary">
                        <span className="urgent-indicator">
                            {unsyncedCount} need{unsyncedCount === 1 ? 's' : ''} sync
                        </span>
                    </div>
                )}
            </Card.Header>
            
            {isExpanded && (
                <Card.Body className="day-content">
                    <div className="job-list">
                        {dayJobs.map((job, index) => {
                            // Task overlay chip — bypasses parseJobInfo/onDisplayDetails
                            // (requestId is null on tasks) and opens the task modal instead.
                            if (job.isTask) {
                                const task = job.task || {};
                                const meta = getTaskTypeMeta(task.task_type);
                                const timeLabel = job.start_time
                                    ? moment(job.start_time, 'HH:mm').format('h:mm A')
                                    : null;
                                return (
                                    <div
                                        key={job.viewKey || `task-${index}`}
                                        className="job-item"
                                        style={{
                                            background: '#F0FDF4',
                                            border: '1px dashed #16A34A',
                                            borderLeft: '4px solid #16A34A',
                                            opacity: job.snoozed ? 0.5 : 1,
                                        }}
                                        onClick={() => onTaskClick && onTaskClick(task)}
                                    >
                                        <div className="job-header">
                                            <div className="job-info">
                                                <div className="job-description">
                                                    <div className="service-name" style={{ color: '#166534' }}>
                                                        <i className={meta.icon} style={{ marginRight: 4 }} />
                                                        {task.title}
                                                        {job.snoozed && <span className="text-muted"> (snoozed)</span>}
                                                    </div>
                                                    <div className="vehicle-info">
                                                        {timeLabel ? `${timeLabel}` : meta.label}
                                                        {task.entity_name ? ` • ${task.entity_name}` : ''}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            }
                            // FIXED: Use robust parsing
                            const { customerName, serviceName, vehicleInfo } = parseJobInfo(job.workString);
                            const multiDayInfo = job.isMultiDay ? ` (Day ${job.currentDay} of ${job.totalDays})` : '';
                            
                            let jobColor = null;
                            let bands = null;
                            const ta = job.tech_assignment;
                            const isMultiTech = ta && (ta.effective_state === 'mixed' || ta.effective_state === 'partial');
                            const techFilterActive = selectedTechnician && selectedTechnician !== 'All Technicians';

                            if (colorMode === 'stage') {
                                jobColor = getStageColor(job.currentState || '', stageColors);
                            } else if (colorMode === 'technician') {
                                if (isMultiTech && techFilterActive) {
                                    // Filtered to one tech — render in that tech's color, not the band.
                                    const me = (ta.techs || []).find(t => t.name === selectedTechnician);
                                    if (me?.user_color) {
                                        jobColor = { wash: getWashFromAccent(me.user_color), accent: me.user_color };
                                    } else {
                                        jobColor = { wash: '#F3F4F6', accent: '#9CA3AF' };
                                    }
                                } else if (isMultiTech) {
                                    bands = getEventBands(ta, technicians);
                                    jobColor = { wash: '#F3F4F6', accent: '#9CA3AF' };
                                } else {
                                    const techKey = job.assigned_technician_id || job.assignedTechnicianId || job.assignedTechnicianName;
                                    if (techKey) {
                                        jobColor = getTechnicianColor(techKey, technicians);
                                    } else {
                                        jobColor = { wash: '#F3F4F6', accent: '#9CA3AF' };
                                    }
                                }
                            }

                            const jobStyle = jobColor
                                ? { background: jobColor.wash, borderLeft: `4px solid ${jobColor.accent}` }
                                : {};
                            if (bands && bands.segments) {
                                const gradient = buildBandGradient(bands.segments);
                                if (gradient) {
                                    jobStyle.borderLeft = '4px solid transparent';
                                    jobStyle.borderImage = `${gradient} 1`;
                                    jobStyle.borderImageSlice = 1;
                                }
                            }
                            const typeClass = jobColor ? '' : (job.requestType === 'dealer' ? 'dealer' : 'installer');

                            // Compute assignee text for the "Assigned to …" detail line.
                            let assigneeText = job.assignedTechnicianName;
                            if (isMultiTech && !techFilterActive) {
                                const techNames = (ta.techs || []).map(t => t.name).filter(Boolean);
                                if (techNames.length === 1) assigneeText = techNames[0];
                                else if (techNames.length === 2) assigneeText = techNames.join(', ');
                                else if (techNames.length >= 3) assigneeText = 'Multiple techs';
                            }

                            return (
                                <div
                                    key={job.viewKey || `${job.requestId}-${job.serviceId}-${index}`}
                                    className={`job-item ${typeClass} ${job.isMultiDay ? 'multi-day-job' : ''}`}
                                    style={jobStyle}
                                    onClick={() => handleJobClick(job)}
                                >
                                    <div className="job-header">
                                        <div className="job-info">
                                            <div className="job-description">
                                                <div className="service-name">
                                                    {serviceName} for {customerName}{multiDayInfo}
                                                    {job.currentState === 'started' && <span className="status-badge started">In Progress</span>}
                                                    {job.currentState === 'finished' && <span className="status-badge completed">Finished</span>}
                                                    {job.currentState === 'collected' && <span className="status-badge completed">Collected</span>}
{!job.isInCalendar && moment(job.startDate).isSameOrAfter(moment().startOf('day')) && <span className="sync-indicator"> ⚠️</span>}                                                </div>
                                                <div className="vehicle-info">
                                                    {isMarineVehicle(job) && (
                                                        <i className="fa-sharp fa-regular fa-sailboat mr-1" title="Boat" />
                                                    )}
                                                    {vehicleInfo}
                                                </div>
                                                {job.totalServicesInJob > 1 && (
                                                    <div className="job-progress">
                                                        Service {job.currentServiceNumber} of {job.totalServicesInJob} for this job
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="job-details">
                                        Due: {job.dueDateString} • Total: {job.estimate || 0} day{job.estimate !== 1 ? 's' : ''}
                                        {job.isMultiDay && ` • Started: ${moment.utc(job.originalWorkDate).local().format("MMM Do")}`}
                                        {assigneeText && (
                                            <span className="assigned-technician"> • Assigned to {assigneeText}{bands && bands.overflow > 0 ? ` +${bands.overflow}` : ''}</span>
                                        )}
                                        {showLocationBadge && (job.location_id || job.locationId) && (
                                            <span className="location-badge" style={{
                                                backgroundColor: getLocationBadgeColor(job.location_id || job.locationId, locations),
                                                color: '#fff',
                                                fontSize: '0.65rem',
                                                padding: '2px 6px',
                                                borderRadius: '3px',
                                                marginLeft: '6px',
                                                fontWeight: 600
                                            }}>
                                                {job.locationName || job.location_name || getLocationName((locations || []).find(l => String(l.location_id) === String(job.location_id || job.locationId)))}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Card.Body>
            )}

            <style>{`
                .day-card {
                    border: 1px solid #e5e7eb !important;
                    border-radius: 8px !important;
                    overflow: hidden;
                    box-shadow: 0 1px 6px rgba(0,0,0,0.08);
                    transition: all 0.3s ease;
                    background: white;
                    margin-bottom: 0;
                }

                .day-card:hover {
                    box-shadow: 0 2px 12px rgba(0,0,0,0.12);
                }

                .day-card.expanded {
                    box-shadow: 0 2px 12px rgba(0,0,0,0.12);
                }

                .day-header {
                    padding: 16px 20px !important;
                    cursor: pointer;
                    transition: background 0.2s ease;
                    border-bottom: 1px solid #e5e7eb !important;
                    background: white !important;
                    border: none !important;
                }

                .day-header:hover {
                    background: #f9fafb !important;
                }

                .day-card.today .day-header {
                    background: #f9fafb !important;
                    position: relative;
                }

                .day-card.today .day-header::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    height: 3px;
                    background: #FF9516;
                }

                .day-date {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                }

                .day-title {
                    font-size: 16px;
                    font-weight: 600;
                    color: #374151;
                }

                .day-count {
                    font-weight: 500;
                    color: #9ca3af;
                }

                .expand-icon {
                    font-size: 14px;
                    color: #6b7280;
                    transition: transform 0.3s ease;
                }

                .day-summary {
                    font-size: 13px;
                    color: #6b7280;
                }

                .urgent-indicator {
                    background: #fff3cd;
                    color: #856404;
                    padding: 2px 6px;
                    border-radius: 4px;
                    font-size: 10px;
                    font-weight: 500;
                    margin-left: 8px;
                    text-transform: uppercase;
                    letter-spacing: 0.3px;
                }

                .day-content {
                    max-height: 0;
                    overflow: hidden;
                    transition: max-height 0.3s ease;
                    border-top: 1px solid #f3f4f6;
                    padding: 0 !important;
                }

                .day-card.expanded .day-content {
                    max-height: 1000px;
                }

                .job-list {
                    padding: 0;
                }

                .job-item {
                    padding: 16px 20px;
                    border-bottom: 1px solid #f3f4f6;
                    transition: all 0.2s ease;
                    border-left: 3px solid transparent;
                    cursor: pointer;
                }

                .job-item:hover {
                    background: #fafafa;
                    transform: translateY(-1px);
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                }

                .job-item:last-child {
                    border-bottom: none;
                }

                .job-item.dealer {
                    border-left-color: #FF9516;
                }

                .job-item.installer {
                    border-left-color: #2E56F6;
                }

                .job-header {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    margin-bottom: 6px;
                }

                .job-info {
                    flex-grow: 1;
                    margin-right: 12px;
                }

                .job-description {
                    line-height: 1.3;
                }

                .service-name {
                    font-size: 15px;
                    font-weight: 600;
                    color: #111827;
                    margin-bottom: 2px;
                    display: flex;
                    align-items: center;
                    flex-wrap: wrap;
                }

                .status-badge {
                    font-size: 10px;
                    font-weight: 600;
                    padding: 2px 8px;
                    border-radius: 4px;
                    text-transform: uppercase;
                    letter-spacing: 0.3px;
                    margin-left: 8px;
                }

                .status-badge.started {
                    background: #fef3c7;
                    color: #92400e;
                }

                .status-badge.completed {
                    background: #dcfce7;
                    color: #166534;
                }

                .sync-indicator {
                    color: #f59e0b;
                    font-size: 14px;
                    margin-left: 4px;
                    opacity: 0.9;
                }

                .vehicle-info {
                    font-size: 13px;
                    font-weight: 500;
                    color: #6b7280;
                    margin-bottom: 2px;
                }

                .job-progress {
                    font-size: 12px;
                    font-weight: 500;
                    color: #9ca3af;
                    font-style: italic;
                }

                .assigned-technician {
                    font-size: 12px;
                    font-weight: 500;
                    color: #059669;
                    background: #ecfdf5;
                    padding: 2px 8px;
                    border-radius: 4px;
                    display: inline;
                    margin-left: 4px;
                }

                .tech-icon {
                    font-size: 11px;
                }

                .multi-day-indicator {
                    color: #6b7280;
                    font-size: 13px;
                    font-weight: 500;
                    margin-left: 8px;
                }

                .job-item.multi-day-job {
                    border-left-width: 4px;
                }

                .job-item.multi-day-job.dealer {
                    background: linear-gradient(90deg, rgba(255, 149, 22, 0.05) 0%, transparent 100%);
                }

                .job-item.multi-day-job.installer {
                    background: linear-gradient(90deg, rgba(46, 86, 246, 0.05) 0%, transparent 100%);
                }

                .job-details {
                    font-size: 13px;
                    color: #6b7280;
                    margin-left: 0;
                }

                /* Mobile adjustments */
                @media (max-width: 768px) {
                    .day-header {
                        padding: 14px 16px !important;
                    }

                    .day-title {
                        font-size: 18px;
                    }

                    .day-summary {
                        font-size: 14px;
                    }

                    .job-item {
                        padding: 18px 16px;
                    }

                    .service-name {
                        font-size: 17px;
                        margin-bottom: 4px;
                    }

                    .vehicle-info {
                        font-size: 15px;
                        margin-bottom: 4px;
                    }

                    .job-progress {
                        font-size: 13px;
                    }

                    .job-details {
                        font-size: 14px;
                        margin-top: 8px;
                    }

                    .sync-indicator {
                        font-size: 16px;
                    }

                    .assigned-technician {
                        font-size: 13px;
                        padding: 3px 10px;
                    }

                    .tech-icon {
                        font-size: 12px;
                    }
                }
            `}</style>
        </Card>
    );
};

export default CalendarCard;